import { useQuery } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import apiService from '../services/api';
import { DEFAULT_MACHINE_STATUS_CONFIG, MachineStatusConfig } from '../constants/machineStatusDefaults';

export interface ResolvedStatus {
  color: string;
  label: string;
  statusText: string;
}

export const useMachineStatusConfig = () => {
  const { data } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const response = await apiService.get('/admin-settings');
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const config: MachineStatusConfig = useMemo(() => {
    const serverConfig = (data as any)?.settings?.machineStatusConfig;
    return serverConfig || DEFAULT_MACHINE_STATUS_CONFIG;
  }, [data]);

  const resolveStatus = useCallback(
    (efficiency: number, isOn: boolean, isStale: boolean | undefined, reason?: string, threshold?: number): ResolvedStatus => {
      // Priority 1: stale → offline
      if (isStale) {
        return config.specialStatuses.offline;
      }

      // Priority 2: machine is on → efficiency-based
      if (isOn) {
        const sorted = [...config.efficiencyRanges].sort((a, b) => b.min - a.min);
        const highestRange = sorted[0];

        // If machine has a custom threshold and efficiency meets it → use the highest range (Running)
        // e.g. old machine with threshold=60%, running at 65% → "Running"
        if (threshold !== undefined && efficiency >= threshold) {
          return { color: highestRange.color, label: highestRange.label, statusText: highestRange.statusText };
        }

        // Otherwise check configured ranges descending
        for (const range of sorted) {
          if (efficiency >= range.min) {
            return { color: range.color, label: range.label, statusText: range.statusText };
          }
        }
        // Fallback to last range (lowest)
        const last = sorted[sorted.length - 1];
        return { color: last.color, label: last.label, statusText: last.statusText };
      }

      // Priority 3: machine is off → reason-based special statuses
      switch (reason) {
        case 'fault':
          return config.specialStatuses.fault;
        case 'manual':
          return config.specialStatuses.manual;
        case 'on':
        case 'ignored':
          // These reasons when machine is off - treat as running (fallback to highest range)
          const highestRange = [...config.efficiencyRanges].sort((a, b) => b.min - a.min)[0];
          return { color: highestRange.color, label: highestRange.label, statusText: highestRange.statusText };
        default:
          return config.specialStatuses.stopped;
      }
    },
    [config]
  );

  return { config, resolveStatus };
};
